import React, { createContext, useState, useEffect, useContext, useCallback } from 'react';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

const AuthContext = createContext();

const CLIENT_ROLE = 5;
const ACTIVE_CLIENT_KEY = 'activeClient';
const HIERARCHY_KEY = 'clientHierarchy';

// Lee el cliente activo persistido en sessionStorage (sobrevive a navegación,
// se borra al cerrar la pestaña — comportamiento equivalente a $_SESSION del PHP).
const readActiveClient = () => {
  try {
    const raw = sessionStorage.getItem(ACTIVE_CLIENT_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
};

const readHierarchy = () => {
  try {
    const raw = sessionStorage.getItem(HIERARCHY_KEY);
    return raw ? JSON.parse(raw) : [];
  } catch {
    return [];
  }
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem('token'));
  const [loading, setLoading] = useState(true);
  // Cliente activo: solo aplica a rol cliente. Si el usuario tiene más de
  // una clínica en su jerarquía, puede cambiar entre ellas con el selector
  // del Sidebar. El id activo viaja en el header X-Active-Client en cada fetch.
  const [activeClient, setActiveClientState] = useState(readActiveClient());
  const [clientHierarchy, setClientHierarchy] = useState(readHierarchy());

  // Cargar la jerarquía del cliente (solo si rol = cliente)
  const loadHierarchy = useCallback(async (tokenArg) => {
    const t = tokenArg || token;
    if (!t) return;
    try {
      const res = await fetch(`${API_URL}/clients/my-hierarchy`, {
        headers: { 'Authorization': `Bearer ${t}` }
      });
      if (!res.ok) return;
      const data = await res.json();
      const list = data.clientes || [];
      setClientHierarchy(list);
      sessionStorage.setItem(HIERARCHY_KEY, JSON.stringify(list));

      // Si no hay activo seleccionado, default a la matriz (primer cliente cuyo
      // id coincida con clinica_matriz del user, o el primero de la lista).
      const current = readActiveClient();
      if (!current && list.length > 0) {
        setActiveClient(list[0]);
      }
    } catch {
      // Silencioso: si falla, el back hace fallback a clinica_matriz del JWT.
    }
  }, [token]);

  useEffect(() => {
    if (token) {
      validateToken();
    } else {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Parche global de fetch (una sola vez, montaje del provider).
  // Lee el activeClient dinámicamente desde sessionStorage en cada llamada
  // — así no depende del closure y no hace falta reinstalar el wrapper en
  // cada cambio de cliente activo.
  useEffect(() => {
    if (window.__cs_fetch_patched) return;
    window.__cs_fetch_patched = true;
    const originalFetch = window.fetch.bind(window);
    window.fetch = (input, init = {}) => {
      try {
        const url = typeof input === 'string' ? input : (input && input.url) || '';
        const goesToApi = typeof url === 'string' && url.startsWith(API_URL);
        if (goesToApi) {
          const raw = sessionStorage.getItem(ACTIVE_CLIENT_KEY);
          const active = raw ? JSON.parse(raw) : null;
          if (active && active.id) {
            const headers = new Headers(init.headers || {});
            if (!headers.has('X-Active-Client')) {
              headers.set('X-Active-Client', String(active.id));
            }
            return originalFetch(input, { ...init, headers });
          }
        }
      } catch {
        // En caso de fallo (sessionStorage no accesible, etc.), pasa intacto.
      }
      return originalFetch(input, init);
    };
    // Sin cleanup: el patch vive mientras la app esté abierta.
  }, []);

  const validateToken = async () => {
    try {
      const res = await fetch(`${API_URL}/auth/me`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setUser(data.user);
        if (data.user && data.user.permisos === CLIENT_ROLE) {
          loadHierarchy(token);
        }
      } else {
        logout();
      }
    } catch (err) {
      logout();
    } finally {
      setLoading(false);
    }
  };

  const login = async (username, password) => {
    const res = await fetch(`${API_URL}/auth/login`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ username, password })
    });
    const data = await res.json();
    if (!res.ok) throw new Error(data.message || 'Error de autenticación');
    setToken(data.token);
    setUser(data.user);
    localStorage.setItem('token', data.token);
    // Resetear estado de cliente activo en cada login.
    sessionStorage.removeItem(ACTIVE_CLIENT_KEY);
    sessionStorage.removeItem(HIERARCHY_KEY);
    setActiveClientState(null);
    setClientHierarchy([]);
    if (data.user && data.user.permisos === CLIENT_ROLE) {
      loadHierarchy(data.token);
    }
    return data;
  };

  const setActiveClient = (client) => {
    if (!client) {
      sessionStorage.removeItem(ACTIVE_CLIENT_KEY);
      setActiveClientState(null);
      return;
    }
    sessionStorage.setItem(ACTIVE_CLIENT_KEY, JSON.stringify(client));
    setActiveClientState(client);
  };

  const logout = () => {
    setToken(null);
    setUser(null);
    setActiveClientState(null);
    setClientHierarchy([]);
    localStorage.removeItem('token');
    sessionStorage.removeItem(ACTIVE_CLIENT_KEY);
    sessionStorage.removeItem(HIERARCHY_KEY);
  };

  return (
    <AuthContext.Provider value={{
      user,
      token,
      loading,
      isAuthenticated: !!user,
      login,
      logout,
      API_URL,
      // Cliente activo + jerarquía (solo relevante para rol cliente con >1 clínica)
      activeClient,
      setActiveClient,
      clientHierarchy,
      loadHierarchy,
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
export default AuthContext;
